import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Progress, Select, Table, message } from 'antd';
import { styled } from 'styled-components';
import { inspections } from '@/api/Inspections/Inspections';
import { QUERY_KEYS } from '@/api/queryKeys';
import { queryClient } from '@/config/reactQuery';
import { useTranslate } from '@/translations';
import {
  Damage,
  DamageAction,
  Inspection,
  PreTimeSuggestionItem,
  PreTimeSuggestionLine,
} from '@/types';
import {
  assignmentOptions,
  isReviewBand,
  needsFrAssignment,
  resolveAssignmentActionId,
  suggestionItemKey,
  unassignedItemKeys,
  withoutAddedFlatRates,
} from './preTimeSuggestionApply';
import { preTimeBandStyle } from './PreTimeRecommendationTag';
import { PreTimeSparkleIcon } from './PreTimeSparkleIcon';
import {
  PRE_TIME_LOOKBACK_YEARS,
  bandLabelKey,
  companionItems,
  itemLabel,
  itemsOfKind,
  usagePercent,
  whereUsedRows,
} from './preTimeSuggestionView';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, 0.9fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SuggestCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  overflow: hidden;
`;

const SuggestHeader = styled.div<{ $background: string }>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${({ $background }) => $background};
`;

const SuggestTitle = styled.h2`
  margin: 0;
  font-size: 16px;
`;

const SuggestBody = styled.p`
  margin: 4px 0 0;
  color: #667085;
  font-size: 13px;
`;

const BandPill = styled.span<{ $color: string }>`
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SideCard = styled.section`
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
`;

const ReasonList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #344054;
`;

const HistoryIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background: #d1e9ff;
  color: #175cd3;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
`;

const HistoryButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 8px 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #175cd3;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const UsageTitle = styled.h3`
  margin: 0;
  font-size: 28px;
`;

const UsageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 20px;
`;

const UsagePill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background: #ecfdf3;
  color: #067647;
  font-weight: 700;
`;

const Companion = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px 48px;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
`;

const existingActions = (damage?: Damage) =>
  (damage?.actions ?? [])
    .filter((action) => {
      const type = action.type?.toLowerCase();
      return type === 'fr' || type === 'labour';
    })
    .map((action: DamageAction) => ({
      id: action.id,
      type: action.type,
      code:
        action.type?.toLowerCase() === 'labour'
          ? action.labourCode
          : action.flatRateCode,
    }));

const ItemUsageDetail: FC<{
  line: PreTimeSuggestionLine;
  item?: PreTimeSuggestionItem;
  damage?: Damage;
  inspection: Inspection;
  onClose: () => void;
}> = ({ line, item, damage, inspection, onClose }) => {
  const { t } = useTranslate();
  const rows = item ? whereUsedRows(line, damage, inspection) : [];
  const companions = item ? companionItems(line, item) : [];
  const percent = item ? usagePercent(item.usedOn, item.sampleSize) : 0;

  return (
    <Modal
      open={!!item}
      title={t('preTimeItemUsageTitle')}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('close')}
        </Button>,
      ]}
      width={640}
      destroyOnHidden
    >
      {item ? (
        <>
          <UsageTitle>{item.code}</UsageTitle>
          {item.description ? <div>{item.description}</div> : null}
          <UsageMeta>
            <UsagePill>{t('preTimeUsageBadge', { percent })}</UsagePill>
            <span>
              {t('preTimeUsedInRepairs', {
                usedOn: item.usedOn,
                sampleSize: item.sampleSize,
              })}
            </span>
          </UsageMeta>
          {rows.length > 0 ? (
            <>
              <strong>{t('preTimeWhereUsed')}</strong>
              <Table
                size="small"
                pagination={false}
                rowKey="labelKey"
                style={{ marginTop: 8 }}
                dataSource={rows}
                columns={[
                  { dataIndex: 'labelKey', render: (key) => t(key) },
                  { dataIndex: 'value' },
                  {
                    align: 'right',
                    render: (_, row) =>
                      `${row.count} (${usagePercent(row.count, row.sampleSize)}%)`,
                  },
                ]}
                showHeader={false}
              />
            </>
          ) : null}
          {companions.length > 0 ? (
            <div style={{ marginTop: 20 }}>
              <strong>{t('preTimeCommonlyUsedWith')}</strong>
              {companions.map((companion) => {
                const companionPercent = usagePercent(
                  companion.usedOn,
                  companion.sampleSize,
                );
                return (
                  <Companion key={suggestionItemKey(companion)}>
                    <span>{itemLabel(companion)}</span>
                    <Progress
                      percent={companionPercent}
                      showInfo={false}
                      strokeColor="#2e90fa"
                    />
                    <span>{companionPercent}%</span>
                  </Companion>
                );
              })}
            </div>
          ) : null}
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 20 }}
            message={t('preTimeStatsFootnote', {
              years: PRE_TIME_LOOKBACK_YEARS,
            })}
          />
        </>
      ) : null}
    </Modal>
  );
};

export const PreTimeSuggestionDetail: FC<{
  line?: PreTimeSuggestionLine;
  damage?: Damage;
  inspection: Inspection;
  canEditFr?: boolean;
  canEditParts?: boolean;
  onClose: () => void;
  onReload?: () => void | Promise<unknown>;
}> = ({
  line,
  damage,
  inspection,
  canEditFr,
  canEditParts,
  onClose,
  onReload,
}) => {
  const { t } = useTranslate();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [blocked, setBlocked] = useState(false);
  const [applying, setApplying] = useState(false);
  const [usageItem, setUsageItem] = useState<PreTimeSuggestionItem>();

  useEffect(() => {
    setSelectedKeys([]);
    setAssignments({});
    setBlocked(false);
    setUsageItem(undefined);
  }, [line?.damageLineId]);

  const selectedItems = useMemo(
    () =>
      line?.items.filter((item) =>
        selectedKeys.includes(suggestionItemKey(item)),
      ) ?? [],
    [line, selectedKeys],
  );
  const options = assignmentOptions(selectedItems, existingActions(damage));
  const missingKeys = line
    ? unassignedItemKeys(line.items, selectedKeys, assignments)
    : [];
  const suggestedItems = withoutAddedFlatRates(
    line?.items ?? [],
    damage?.actions ?? [],
  );
  const historical = [...suggestedItems].sort(
    (left, right) => right.usedOn - left.usedOn,
  );

  const clear = () => {
    setSelectedKeys([]);
    setAssignments({});
    setBlocked(false);
  };

  const close = () => {
    clear();
    setUsageItem(undefined);
    onClose();
  };

  const apply = async () => {
    if (!line || !isReviewBand(line.band)) return;
    if (missingKeys.length > 0) {
      setBlocked(true);
      return;
    }
    const needsActions = selectedItems.some(
      (item) => item.kind === 'FR' || item.kind === 'Labour',
    );
    const needsParts = selectedItems.some((item) =>
      needsFrAssignment(item.kind),
    );
    if ((needsActions && !canEditFr) || (needsParts && !canEditParts)) return;

    setApplying(true);
    setBlocked(false);
    const beforeIds = new Set(
      (damage?.actions ?? []).map((action) => action.id),
    );
    const postActions = async (type: 'FR' | 'Labour') => {
      const group = selectedItems.filter((item) => item.kind === type);
      if (group.length === 0) return true;
      const res = await inspections.addInspectionsVehicleDamageActions({
        inspectionId: inspection.id,
        type,
        items: group.map((item) => ({
          referenceId: item.referenceId,
          description: item.description ?? '',
          quantity: 1,
          price: item.price,
          damageLineIds: [line.damageLineId],
        })),
      });
      if (!res.success) {
        message.error(res.message?.Message);
        return false;
      }
      return true;
    };

    try {
      if (!(await postActions('FR'))) return;
      if (!(await postActions('Labour'))) {
        await onReload?.();
        return;
      }
      if (needsParts) {
        const fresh = await inspections.getInspectionsVehiclesDamagesLines(
          inspection.id,
        );
        const updated = (fresh.data ?? []).find(
          (row) => row.id === line.damageLineId,
        );
        const created = (updated?.actions ?? [])
          .filter((action) => !beforeIds.has(action.id))
          .map((action) => ({
            id: action.id,
            type: action.type,
            referenceId: action.referenceId,
          }));
        const parts = [];
        for (const item of selectedItems.filter((entry) =>
          needsFrAssignment(entry.kind),
        )) {
          const key = suggestionItemKey(item);
          const option = options.find(
            (candidate) => candidate.value === assignments[key],
          );
          const actionId = resolveAssignmentActionId(option, created);
          if (!actionId) {
            message.error(t('preTimeAssignRequired'));
            await onReload?.();
            return;
          }
          parts.push({
            damageActionIds: [actionId],
            type: item.type,
            referenceId: item.referenceId,
            quantity: 1,
            price: item.price,
            description: item.description,
          });
        }
        const res = await inspections.addInspectionsVehicleDamageParts({
          inspectionId: inspection.id,
          parts,
        });
        if (!res.success) {
          message.error(res.message?.Message);
          await onReload?.();
          return;
        }
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.preTimeSuggestions, inspection.id],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.damages, inspection.id],
        }),
        onReload?.(),
      ]);
      const suggestions = queryClient.getQueryData<PreTimeSuggestionLine[]>([
        QUERY_KEYS.preTimeSuggestions,
        inspection.id,
      ]);
      const next = suggestions?.find(
        (suggestion) => suggestion.damageLineId === line.damageLineId,
      );
      message.success(t('preTimeApplied'));
      if (!next || next.items.length === 0) close();
      else clear();
    } finally {
      setApplying(false);
    }
  };

  const renderItems = (
    title: string,
    items: PreTimeSuggestionItem[],
    empty?: string,
  ) => (
    <Section>
      <strong>{title}</strong>
      {items.length === 0 && empty ? (
        <Alert type="info" showIcon message={empty} />
      ) : (
        <Table
          size="small"
          pagination={false}
          rowKey={(item) => suggestionItemKey(item)}
          dataSource={items}
          rowSelection={{
            selectedRowKeys: selectedKeys.filter((key) =>
              items.some((item) => suggestionItemKey(item) === key),
            ),
            onChange: (keys) => {
              const tableKeys = new Set(items.map(suggestionItemKey));
              const next = [
                ...selectedKeys.filter((key) => !tableKeys.has(key)),
                ...keys.map(String),
              ];
              setSelectedKeys(next);
              setAssignments((current) =>
                Object.fromEntries(
                  Object.entries(current).filter(([key]) => next.includes(key)),
                ),
              );
            },
          }}
          columns={[
            {
              title: t('preTimeCode'),
              dataIndex: 'code',
              render: (_, item) =>
                item.supplierCode
                  ? `${item.supplierCode} ${item.code}`
                  : item.code,
            },
            { title: t('description'), dataIndex: 'description' },
            {
              title: t('preTimeUsage'),
              render: (_, item) =>
                `${usagePercent(item.usedOn, item.sampleSize)}% (${item.usedOn}/${item.sampleSize})`,
            },
          ]}
        />
      )}
      {items
        .filter(
          (item) =>
            selectedKeys.includes(suggestionItemKey(item)) &&
            needsFrAssignment(item.kind),
        )
        .map((item) => {
          const key = suggestionItemKey(item);
          return (
            <Select
              key={key}
              aria-label={t('preTimeAssignFr')}
              placeholder={t('preTimeAssignFr')}
              status={
                blocked && missingKeys.includes(key) ? 'error' : undefined
              }
              value={assignments[key]}
              options={options.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              onChange={(value) =>
                setAssignments((current) => ({ ...current, [key]: value }))
              }
            />
          );
        })}
    </Section>
  );

  const band = line?.band ?? 'Manual';
  const bandStyle = preTimeBandStyle[band];
  const flatRates = itemsOfKind(suggestedItems, ['FR', 'Labour']);
  const parts = itemsOfKind(suggestedItems, ['Part', 'Supplier']);
  const materials = itemsOfKind(suggestedItems, ['Material']);

  return (
    <>
      <Modal
        open={!!line}
        title={
          <SuggestHeader $background={bandStyle.background}>
            <div>
              <SuggestTitle>
                <PreTimeSparkleIcon color={bandStyle.color} />{' '}
                {t('preTimeSuggestedTitle')}
              </SuggestTitle>
              <SuggestBody>
                {t('preTimeBasedOn', {
                  sampleSize: line?.sampleSize ?? 0,
                  years: PRE_TIME_LOOKBACK_YEARS,
                })}
              </SuggestBody>
            </div>
            <BandPill $color={bandStyle.color}>
              {t(bandLabelKey[band])}
            </BandPill>
          </SuggestHeader>
        }
        footer={
          <Footer>
            <Button danger onClick={clear}>
              {t('preTimeClearSuggestions')}
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={close}>{t('cancel')}</Button>
              <Button
                type="primary"
                loading={applying}
                disabled={
                  !line ||
                  !isReviewBand(line.band) ||
                  selectedItems.length === 0
                }
                onClick={() => void apply()}
              >
                {t('preTimeApplySelected')}
              </Button>
            </div>
          </Footer>
        }
        width={1040}
        closable={false}
        destroyOnHidden
      >
        {line ? (
          <>
            <Layout>
              <div>
                <SuggestCard>
                  {renderItems(
                    t('preTimeFrCodes', { count: flatRates.length }),
                    flatRates,
                    t('preTimeNoFrCodes'),
                  )}
                  {renderItems(
                    t('preTimePartsCount', { count: parts.length }),
                    parts,
                    t('preTimeNoParts'),
                  )}
                  {renderItems(
                    t('preTimeMaterialsCount', { count: materials.length }),
                    materials,
                    t('preTimeNoMaterials'),
                  )}
                </SuggestCard>
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                <SideCard>
                  <strong>{t('preTimeWhyTitle')}</strong>
                  <ReasonList>
                    <li>
                      {t('preTimeSimilarLines', { count: line.sampleSize })}
                    </li>
                    {whereUsedRows(line, damage, inspection).map((row) => (
                      <li key={row.labelKey}>
                        {t(row.labelKey)}: {row.value}
                      </li>
                    ))}
                  </ReasonList>
                  <SuggestBody>
                    {line.band === 'Manual'
                      ? t('preTimeManualBody')
                      : t('preTimeWhyHighUsage')}
                  </SuggestBody>
                </SideCard>
                <SideCard>
                  <strong>{t('preTimeHistoricalUsage')}</strong>
                  {historical.length === 0 ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginTop: 8 }}
                      message={t('preTimeNoHistorical')}
                    />
                  ) : (
                    <>
                      <SuggestBody>{t('preTimeTopCombinations')}</SuggestBody>
                      {historical.slice(0, 3).map((item, index) => (
                        <HistoryButton
                          key={suggestionItemKey(item)}
                          type="button"
                          onClick={() => setUsageItem(item)}
                        >
                          <div className="d-flex align-items-center">
                            <HistoryIndex>{index + 1}</HistoryIndex>
                            <span>{item.code}</span>
                          </div>
                          <span>
                            {usagePercent(item.usedOn, item.sampleSize)}%
                          </span>
                        </HistoryButton>
                      ))}
                      <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        onClick={() =>
                          historical[0] && setUsageItem(historical[0])
                        }
                      >
                        {t('preTimeViewAllMatches', {
                          count: line.sampleSize,
                        })}
                      </Button>
                    </>
                  )}
                </SideCard>
              </div>
            </Layout>
            {blocked ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={t('preTimeAssignRequired')}
              />
            ) : null}
          </>
        ) : null}
      </Modal>
      {line && historical.length > 0 ? (
        <ItemUsageDetail
          line={line}
          item={usageItem}
          damage={damage}
          inspection={inspection}
          onClose={() => setUsageItem(undefined)}
        />
      ) : null}
    </>
  );
};
